import { ref, computed } from 'vue';
import api from '@/helpers/apiClient';
import { useAuthStore } from '@/stores/auth';

// Singleton state — shared across all components that call usePlanFeatures()
const plan = ref(null);
const features = ref([]);
const loaded = ref(false);
const loading = ref(false);

/**
 * Composable for plan-based feature gating.
 * Fetches the feature list from GET /api/plan/features once per session
 * and exposes reactive plan/features + a hasFeature(name) helper.
 */
export function usePlanFeatures() {
  const authStore = useAuthStore();

  async function fetchFeatures() {
    if (loading.value) return;
    loading.value = true;
    try {
      const { data } = await api.get('/api/plan/features');
      plan.value = data.plan;
      features.value = data.features;
      loaded.value = true;
    } catch {
      // If fetch fails (e.g. not authenticated), default to básico
      plan.value = authStore.empresa?.plan || 'básico';
      features.value = [];
      loaded.value = false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Check if a feature is available in the current plan.
   * Superadmins always return true.
   */
  function hasFeature(feature) {
    if (authStore.isSuperAdmin) return true;
    return features.value.includes(feature);
  }

  /**
   * Initialize — call once when the app authenticates.
   * Safe to call multiple times; only fetches once.
   */
  async function init() {
    if (loaded.value || loading.value) return;
    await fetchFeatures();
  }

  /**
   * Force refresh (e.g. after plan upgrade).
   */
  async function refresh() {
    loaded.value = false;
    await fetchFeatures();
  }

  /**
   * Reset state on logout.
   */
  function reset() {
    plan.value = null;
    features.value = [];
    loaded.value = false;
    loading.value = false;
  }

  const isPro = computed(() => plan.value === 'pro');

  return {
    plan,
    features,
    loaded,
    loading,
    isPro,
    hasFeature,
    init,
    refresh,
    reset,
  };
}
